import customersService from '@/services/customers';
import generalCatalogsService from '@/services/generalCatalogs';
import simpleNotesService from '@/services/simpleNotes';
import simpleNotePaymentPlansService from '@/services/simpleNotePaymentPlans';
import { extractApiErrorMessage, extractCollection, extractPayload } from '@/utils/dataNormalization';
import { normalizeFiscalCodeOptions } from '@/composables/invoices/create/helpers';

const createEmptyConcept = () => ({
  description: '',
  qty: 1,
  unit: 'Servicio',
  unitPrice: 0,
  paymentId: 0,
  saleId: 0,
});

export const simpleNoteCreateMethods = {
  async initializePage() {
    this.prefillPaymentId = this.resolvePrefillPaymentId();
    await Promise.all([
      this.loadCustomerSelectors(),
      this.loadFiscalCatalogs(),
      this.loadNextFolio(),
    ]);
    await this.applyPaymentPlanPrefillIfNeeded();
  },
  resolvePrefillPaymentId() {
    const paymentId = Number(this.$route?.query?.payment_id || this.$route?.query?.paymentId || 0);
    return Number.isFinite(paymentId) && paymentId > 0 ? Math.floor(paymentId) : 0;
  },
  async loadCustomerSelectors() {
    try {
      const response = await customersService.selectors();
      const items = extractCollection(response);

      const mapped = (Array.isArray(items) ? items : [])
        .map((item) => {
          const id = Number(item?.id || item?.value || 0) || 0;
          const name = String(item?.name || item?.label || '').trim();
          if (id <= 0 || name === '') {
            return null;
          }

          return {
            value: id,
            label: name,
            customerId: id,
            meta: {
              id,
              customerId: id,
              businessName: name,
              clientName: name,
            },
          };
        })
        .filter((option) => Number(option?.value || 0) > 0 && String(option?.label || '').trim() !== '');

      this.customerOptions = mapped;
    } catch (error) {
      this.customerOptions = [];
    }
  },
  async loadFiscalCatalogs() {
    try {
      const [regimensResult, usosResult, formasResult, metodosResult] = await Promise.allSettled([
        generalCatalogsService.getRegimens(),
        generalCatalogsService.getUsosCfdi(),
        generalCatalogsService.getFormasPago(),
        generalCatalogsService.getMetodosPago(),
      ]);

      if (regimensResult.status === 'fulfilled') {
        normalizeFiscalCodeOptions(extractCollection(regimensResult.value));
      }

      if (usosResult.status === 'fulfilled') {
        normalizeFiscalCodeOptions(extractCollection(usosResult.value));
      }

      if (formasResult.status === 'fulfilled') {
        this.paymentFormOptions = normalizeFiscalCodeOptions(extractCollection(formasResult.value));
      }

      if (metodosResult.status === 'fulfilled') {
        normalizeFiscalCodeOptions(extractCollection(metodosResult.value));
      }

      const cashOption = this.paymentFormOptions.find((item) => String(item?.value || '') === '01');
      const selectedPaymentForm = String(cashOption?.value || this.paymentFormOptions[0]?.value || '01').trim() || '01';

      this.noteInfo = {
        ...this.noteInfo,
        paymentForm: selectedPaymentForm,
      };
    } catch (error) {
      this.paymentFormOptions = [];
    }
  },
  async loadNextFolio() {
    try {
      const response = await simpleNotesService.nextFolio({}, { skipLoading: true });
      const payload = extractPayload(response);
      const data = payload && typeof payload === 'object' && payload.data && typeof payload.data === 'object'
        ? payload.data
        : payload;
      const nextFolio = String(data?.folio || '').trim();

      if (nextFolio === '') {
        throw new Error('simple_note_next_folio_empty');
      }

      this.noteInfo = {
        ...this.noteInfo,
        folio: nextFolio,
      };
    } catch (error) {
      this.noteInfo = {
        ...this.noteInfo,
        folio: String(this.noteInfo?.folio || '-'),
      };
    }
  },
  async ensureReceiverSelectedByCustomer(customerId) {
    const resolvedCustomerId = Number(customerId || 0) || 0;
    if (resolvedCustomerId <= 0) {
      return false;
    }

    const options = Array.isArray(this.customerOptions) ? this.customerOptions : [];
    const receiver = options.find((option) => {
      const optionCustomerId = Number(
        option?.customerId
        || option?.meta?.customerId
        || option?.meta?.companyId
        || option?.value
        || 0
      ) || 0;

      return optionCustomerId === resolvedCustomerId;
    }) || null;

    if (!receiver) {
      return false;
    }

    this.selectedReceiver = receiver;
    return true;
  },
  buildPrefilledSimpleNoteConceptFromPayment(payment = {}) {
    const paymentId = Number(payment?.id || 0) || 0;
    if (paymentId <= 0) {
      return null;
    }

    const unitCode = 'E48';
    const unitName = 'Unidad de servicio';

    return {
      serviceId: Number(payment?.product_id || 0) || null,
      productId: Number(payment?.product_id || 0) || null,
      paymentId,
      saleId: Number(payment?.sale_id || 0) || 0,
      qty: 1,
      unit: `${unitCode} - ${unitName}`,
      unitValue: unitCode,
      unitCode,
      unitId: 0,
      code: '81112100',
      description: String(payment?.concept || '').trim() || 'Concepto de plan de pago',
      unitPrice: Number(payment?.subtotal || payment?.amount || 0) || 0,
      sourceMode: 'payment-plan',
      sourcePaymentIds: [paymentId],
      isPaymentPlanMerged: false,
    };
  },
  async applyPaymentPlanPrefillIfNeeded() {
    const paymentId = Number(this.prefillPaymentId || 0) || 0;
    if (paymentId <= 0) {
      return;
    }

    try {
      const response = await simpleNotePaymentPlansService.show(paymentId, {}, { skipLoading: true });
      const payload = extractPayload(response);
      const payment = payload && typeof payload === 'object' && payload.data && typeof payload.data === 'object'
        ? payload.data
        : payload;

      if (!payment || Number(payment?.id || 0) <= 0) {
        return;
      }

      const customerId = Number(payment?.company_id || payment?.customer_id || 0) || 0;
      if (customerId > 0) {
        await this.ensureReceiverSelectedByCustomer(customerId);
      }

      const prefilledConcept = this.buildPrefilledSimpleNoteConceptFromPayment(payment);
      if (!prefilledConcept) {
        return;
      }

      const conceptPaymentId = Number(prefilledConcept?.paymentId || 0) || 0;
      const hasPrefilledConcept = Array.isArray(this.concepts) && this.concepts.some((concept) => {
        return Number(concept?.paymentId || concept?.payment_id || 0) === conceptPaymentId;
      });

      if (!hasPrefilledConcept) {
        this.concepts = [...this.concepts, prefilledConcept];
      }
    } catch (error) {
      // Keep page usable when prefill cannot be resolved.
    }
  },
  onHeaderAction(action) {
    const actionKey = String(action?.key || '').trim();
    if (actionKey === 'back') {
      this.goBack();
    }
  },
  selectReceiver(customer) {
    const previousCustomerId = Number(
      this.selectedReceiver?.meta?.customerId
      || this.selectedReceiver?.customerId
      || this.selectedReceiver?.value
      || 0
    ) || 0;

    const nextReceiver = customer && typeof customer === 'object' ? customer : null;
    const nextCustomerId = Number(
      nextReceiver?.meta?.customerId
      || nextReceiver?.customerId
      || nextReceiver?.value
      || 0
    ) || 0;

    this.selectedReceiver = nextReceiver;

    if (previousCustomerId !== nextCustomerId) {
      this.concepts = [];
    }
  },
  async createInlineCustomer(payload) {
    const inlineData = payload && typeof payload === 'object' ? payload : {};
    const customerName = String(inlineData?.name || '').trim();
    if (customerName === '') {
      return;
    }

    if (this.creatingInlineCustomer) {
      return;
    }

    this.creatingInlineCustomer = true;

    try {
      const response = await customersService.store({
        name: customerName,
      });

      const payloadData = extractPayload(response);
      const data = payloadData && typeof payloadData === 'object' && payloadData.data && typeof payloadData.data === 'object'
        ? payloadData.data
        : payloadData;

      const id = Number(data?.id || data?.customer_id || 0) || 0;
      const name = String(data?.name || customerName).trim();
      if (id <= 0 || name === '') {
        throw new Error('customer_create_invalid');
      }

      const option = {
        value: id,
        label: name,
        customerId: id,
        meta: {
          id,
          customerId: id,
          businessName: name,
          clientName: name,
        },
      };

      this.customerOptions = [...this.customerOptions, option]
        .sort((left, right) => String(left?.label || '').localeCompare(String(right?.label || ''), 'es'));
      this.selectedReceiver = option;
      this.$toast?.success(String(this.text?.create?.toast?.customerCreated || '').trim());
    } catch (error) {
      const message = error?.response?.data?.message
        || String(this.text?.create?.toast?.customerCreateError || '').trim();
      this.$toast?.error(message);
    } finally {
      this.creatingInlineCustomer = false;
    }
  },
  onNoteInfoChange(payload) {
    const key = String(payload?.key || '').trim();
    if (key === '') {
      return;
    }

    this.noteInfo = {
      ...this.noteInfo,
      [key]: payload?.value,
    };
  },
  onCommentsChange(value) {
    this.comments = String(value || '');
  },
  addConcept() {
    const selectedCustomerId = Number(
      this.selectedReceiver?.meta?.customerId
      || this.selectedReceiver?.customerId
      || this.selectedReceiver?.meta?.id
      || this.selectedReceiver?.value
      || 0
    ) || 0;

    if (selectedCustomerId <= 0) {
      this.$toast?.error(String(this.text?.create?.validation?.customerRequiredForPlans || '').trim());
      return;
    }

    this.conceptModalMode = 'create';
    this.editingConceptIndex = -1;
    this.showConceptModal = true;
  },
  editConcept(payload) {
    const index = Number(payload?.index);
    if (!Number.isFinite(index) || index < 0 || index >= this.concepts.length) {
      return;
    }

    this.conceptModalMode = 'edit';
    this.editingConceptIndex = index;
    this.showConceptModal = true;
  },
  deleteConcept(index) {
    const normalizedIndex = Number(index);
    if (!Number.isFinite(normalizedIndex) || normalizedIndex < 0 || normalizedIndex >= this.concepts.length) {
      return;
    }

    this.concepts = this.concepts.filter((_, rowIndex) => rowIndex !== normalizedIndex);
  },
  closeConceptModal() {
    this.showConceptModal = false;
    this.conceptModalMode = 'create';
    this.editingConceptIndex = -1;
  },
  extractPaymentPlanIdsFromConcept(concept = {}) {
    const explicitIds = Array.isArray(concept?.sourcePaymentIds)
      ? concept.sourcePaymentIds
      : [];
    const parsedExplicit = explicitIds
      .map((value) => Number(value || 0) || 0)
      .filter((value) => value > 0);

    if (parsedExplicit.length > 0) {
      return [...new Set(parsedExplicit)];
    }

    const paymentId = Number(concept?.paymentId || concept?.payment_id || 0) || 0;
    return paymentId > 0 ? [paymentId] : [];
  },
  isPaymentPlanConcept(concept = {}) {
    const sourceMode = String(concept?.sourceMode || '').trim().toLowerCase();
    if (sourceMode === 'payment-plan') {
      return true;
    }

    return this.extractPaymentPlanIdsFromConcept(concept).length > 0;
  },
  submitConceptFromModal(conceptPayload = {}) {
    const incomingConcepts = Array.isArray(conceptPayload?.concepts)
      ? conceptPayload.concepts
      : [conceptPayload];
    const payloadSourceMode = String(conceptPayload?.sourceMode || '').trim().toLowerCase();

    const normalizedConcepts = incomingConcepts
      .map((item) => {
        const sourcePaymentIds = Array.isArray(item?.sourcePaymentIds)
          ? [...new Set(item.sourcePaymentIds
            .map((value) => Number(value || 0) || 0)
            .filter((value) => value > 0))]
          : [];

        return {
          serviceId: Number(item?.serviceId || item?.productId || 0) || null,
          productId: Number(item?.productId || item?.serviceId || 0) || null,
          paymentId: Number(item?.paymentId || item?.payment_id || 0) || 0,
          saleId: Number(item?.saleId || item?.sale_id || 0) || 0,
          qty: Number(item?.qty || 0) || 1,
          unit: String(item?.unit || '').trim(),
          unitValue: String(item?.unitValue || '').trim(),
          unitCode: String(item?.unitCode || '').trim(),
          unitId: Number(item?.unitId || 0) || 0,
          code: String(item?.code || '').trim(),
          description: String(item?.description || '').trim(),
          unitPrice: Number(item?.unitPrice || 0) || 0,
          sourceMode: String(item?.sourceMode || payloadSourceMode || '').trim().toLowerCase(),
          sourcePaymentIds,
          isPaymentPlanMerged: Boolean(item?.isPaymentPlanMerged),
        };
      })
      .filter((item) => {
        return Number(item?.unitPrice || 0) > 0
          || String(item?.description || '').trim() !== ''
          || Number(item?.productId || 0) > 0
          || Number(item?.paymentId || 0) > 0;
      });

    if (normalizedConcepts.length === 0) {
      return;
    }

    if (payloadSourceMode === 'payment-plan') {
      const retainedConcepts = this.concepts.filter((concept) => !this.isPaymentPlanConcept(concept));
      this.concepts = [...retainedConcepts, ...normalizedConcepts];
      this.closeConceptModal();
      return;
    }

    if (this.conceptModalMode === 'edit' && this.editingConceptIndex >= 0) {
      const [firstConcept, ...restConcepts] = normalizedConcepts;
      this.concepts = this.concepts.map((item, index) => {
        return index === this.editingConceptIndex ? { ...item, ...firstConcept } : item;
      });

      if (restConcepts.length > 0) {
        this.concepts = [...this.concepts, ...restConcepts];
      }
    } else {
      this.concepts = [...this.concepts, ...normalizedConcepts];
    }

    this.closeConceptModal();
  },
  resetDraft() {
    this.concepts = [];
    this.comments = '';
    this.showConceptModal = false;
    this.conceptModalMode = 'create';
    this.editingConceptIndex = -1;

      const message = String(this.text?.create?.toast?.conceptsCleared || '').trim();
    if (message !== '') {
      this.$toast?.success(message);
    }
  },
  previewSimpleNote() {
    const payload = this.buildPayload();

    if (payload.customer_id <= 0) {
      this.$toast?.error(String(this.text?.create?.validation?.customerRequired || '').trim());
      return;
    }

    if (!Array.isArray(payload.concepts) || payload.concepts.length === 0) {
      this.$toast?.error(String(this.text?.create?.validation?.conceptsRequired || '').trim());
      return;
    }

    const previewPayload = {
      ...payload,
      folio: String(this.noteInfo?.folio || '').trim(),
    };

    simpleNotesService.previewPdf(previewPayload)
      .then((response) => {
        this.openPdfBlob(response?.data ?? response);
      })
      .catch((error) => {
        const fallback = String(this.text?.create?.toast?.previewError || '').trim();
        this.$toast?.error(extractApiErrorMessage(error, fallback) || fallback);
      });
  },
  openPdfBlob(payload) {
    const blob = payload instanceof Blob
      ? payload
      : new Blob([payload], { type: 'application/pdf' });

    const objectUrl = window.URL.createObjectURL(blob);
    const opened = window.open(objectUrl, '_blank', 'noopener');

    if (!opened) {
      const link = window.document.createElement('a');
      link.href = objectUrl;
      link.download = 'vista_previa_nota_simple.pdf';
      link.click();
    }

    window.setTimeout(() => {
      window.URL.revokeObjectURL(objectUrl);
    }, 60000);
  },
  buildConceptsPayload() {
    return this.concepts
      .map((concept) => {
        const quantity = Number(concept?.qty || 0) || 0;
        const price = Number(concept?.unitPrice || 0) || 0;
        const amount = quantity * price;
        const sourcePaymentIds = this.extractPaymentPlanIdsFromConcept(concept);

        return {
          description: String(concept?.description || '').trim(),
          quantity,
          unit: String(concept?.unit || '').trim() || 'Servicio',
          price,
          import: amount,
          payment_id: Number(concept?.paymentId || concept?.payment_id || 0) || 0,
          sale_id: Number(concept?.saleId || concept?.sale_id || 0) || 0,
          source_payment_ids: sourcePaymentIds,
        };
      })
      .filter((concept) => concept.description !== '' && concept.import > 0);
  },
  buildPayload() {
    const customerId = Number(
      this.selectedReceiver?.meta?.customerId
      || this.selectedReceiver?.customerId
      || this.selectedReceiver?.meta?.companyId
      || this.selectedReceiver?.companyId
      || 0
    ) || 0;

    return {
      customer_id: customerId,
      date: String(this.noteInfo?.date || '').trim(),
      currency: String(this.noteInfo?.currency || 'MXN').trim().toUpperCase() || 'MXN',
      exchange_rate: 1,
      payment_form: String(this.noteInfo?.paymentForm || '').trim(),
      comments: String(this.comments || '').trim(),
      concepts: this.buildConceptsPayload(),
    };
  },
  async submitSimpleNote() {
    if (this.submittingSimpleNote) {
      return;
    }

    const payload = this.buildPayload();

    if (payload.customer_id <= 0) {
      this.$toast?.error(String(this.text?.create?.validation?.customerRequired || '').trim());
      return;
    }

    if (!Array.isArray(payload.concepts) || payload.concepts.length === 0) {
      this.$toast?.error(String(this.text?.create?.validation?.conceptsRequired || '').trim());
      return;
    }

    this.submittingSimpleNote = true;
    try {
      const response = await simpleNotesService.store(payload);
      const apiErrorMessage = extractApiErrorMessage(response, '');
      if (apiErrorMessage !== '') {
        this.$toast?.error(apiErrorMessage);
        return;
      }

      const data = extractPayload(response);
      const simpleNoteId = Number(data?.id || 0) || 0;
      this.$toast?.success(String(this.text?.create?.toast?.created || '').trim());

      if (simpleNoteId > 0) {
        this.$router.push({
          name: 'simpleNoteDetail',
          params: { simpleNoteId: String(simpleNoteId) },
        });
        return;
      }

      this.goBack();
    } catch (error) {
      const fallback = String(this.text?.create?.toast?.createError || '').trim();
      this.$toast?.error(extractApiErrorMessage(error, fallback) || fallback);
    } finally {
      this.submittingSimpleNote = false;
    }
  },
  goBack() {
    this.$router.push({ name: 'simpleNotes' });
  },
  createEmptyConcept,
};
